import React, { Component, ErrorInfo } from 'react';
import { PrimaryButton, mergeStyleSets } from '@fluentui/react';
import { Warning, XCircle } from '@phosphor-icons/react';

interface Props {
  children: React.ReactNode;
  /** Compact mode shows a small inline error instead of a full-page recovery UI */
  compact?: boolean;
  /** Label shown in compact mode to identify what failed */
  label?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const s = mergeStyleSets({
  fullPage: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
    padding: 40,
    textAlign: 'center' as const,
  },
  iconWrap: {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: '#FDE8E8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: '#232323',
    marginBottom: 6,
  },
  message: {
    fontSize: 13,
    color: '#747474',
    maxWidth: 400,
    lineHeight: '1.5',
    marginBottom: 16,
  },
  detail: {
    fontSize: 11,
    color: '#A0A0A0',
    fontFamily: 'monospace',
    maxWidth: 500,
    wordBreak: 'break-word' as const,
    marginBottom: 16,
  },
  compact: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '16px 12px',
    color: '#D13438',
    fontSize: 12,
    background: '#FDE8E8',
    borderRadius: 8,
    height: '100%',
    minHeight: 80,
  },
  retryBtn: {
    fontSize: 11,
    padding: '2px 10px',
    minWidth: 0,
    height: 24,
    border: '1px solid #D13438',
    background: 'transparent',
    color: '#D13438',
    borderRadius: 4,
    cursor: 'pointer',
  },
});

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.compact) {
      return (
        <div className={s.compact}>
          <Warning size={14} />
          <span>{this.props.label || 'Widget'} failed to render</span>
          <button className={s.retryBtn} onClick={this.handleRetry}>Retry</button>
        </div>
      );
    }

    return (
      <div className={s.fullPage}>
        <div className={s.iconWrap}>
          <XCircle size={24} color="#D13438" />
        </div>
        <div className={s.title}>Something went wrong</div>
        <div className={s.message}>
          An unexpected error occurred. You can try refreshing this section, or go back and try again.
        </div>
        {this.state.error && (
          <div className={s.detail}>{this.state.error.message}</div>
        )}
        <PrimaryButton
          text="Try Again"
          onClick={this.handleRetry}
          styles={{ root: { background: '#2e873d', border: 'none' }, rootHovered: { background: '#256d31' } }}
        />
      </div>
    );
  }
}
